// Iter-190 — Mission profitability widget (CEO/GESTIONNAIRE see full; Manager sees aggregate-only).
// Live KPI cards (CA / Coût / Marge / %) + toggle Projection ↔ Actual + per-assignment table.
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Calculator, ChevronDown, ChevronRight, Eye, EyeOff, Loader2, RefreshCw, TrendingUp } from "lucide-react";

const fmtMad = (n) =>
  n === null || n === undefined ? "—" : `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} MAD`;

const marginTone = (pct) => {
  if (pct === null || pct === undefined) return "text-zinc-500";
  if (pct >= 30) return "text-emerald-700";
  if (pct >= 15) return "text-amber-700";
  return "text-red-700";
};

export default function ProfitabilityWidget({ missionId, refreshKey = 0 }) {
  const [mode, setMode] = useState("projected");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  // Iter-191i — expanded monthly-breakdown rows (set of assignment_id).
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/missions/${missionId}/profitability?mode=${mode}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setData({ forbidden: true });
      } else {
        toast.error(e?.response?.data?.detail || "Erreur de calcul");
      }
    } finally {
      setLoading(false);
    }
  }, [missionId, mode]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (data?.forbidden) return null;
  if (!data && !loading) return null;

  const margin = data?.margin;
  const marginPct = data?.margin_pct;
  const revenue = data?.revenue?.total;
  const cost = data?.cost?.total;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-5 mb-6" data-testid="profitability-widget">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
          <Calculator size={12} /> Rentabilité
          {data?.redacted && <span className="text-amber-700 normal-case tracking-normal">· vue manager (agrégée)</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex border border-zinc-200 rounded-md overflow-hidden text-xs" data-testid="profitability-mode-toggle">
            <button
              onClick={() => setMode("projected")}
              data-testid="profitability-mode-projected"
              className={`px-3 h-8 ${mode === "projected" ? "bg-brand-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
            >Projection</button>
            <button
              onClick={() => setMode("actual")}
              data-testid="profitability-mode-actual"
              className={`px-3 h-8 border-l border-zinc-200 ${mode === "actual" ? "bg-brand-navy text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
            >Réel (CRA)</button>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="profitability-refresh"
            className="inline-flex items-center justify-center w-8 h-8 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 disabled:opacity-60"
            title="Recalculer"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          </button>
        </div>
      </div>

      {/* Iter-191e — FX banner: show conversion rate when mission currency isn't MAD. */}
      {data && data.currency && data.currency !== "MAD" && data.fx_rate_to_mad && data.fx_rate_to_mad !== 1 && (
        <div className="mb-3 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="profitability-fx-banner">
          <RefreshCw size={11} className="mt-0.5 shrink-0" />
          <div>
            Mission en <strong>{data.currency}</strong>. Conversion appliquée pour la rentabilité (CA &amp; TJM affichés en MAD) — taux <strong>1 {data.currency} = {data.fx_rate_to_mad} MAD</strong>.
            Le taux est configurable dans <em>Settings → Payroll</em>.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profitability-kpis">
        <Kpi label="Chiffre d'affaires" value={fmtMad(revenue)} hint={data?.revenue?.forfait_revenue ? "Forfait (fixe)" : "Régie × jours"} testid="profitability-kpi-revenue" />
        <Kpi label="Coût total" value={cost === null ? "—" : fmtMad(cost)} hint={data?.redacted ? "Détail masqué" : "CJM × jours"} testid="profitability-kpi-cost" />
        <Kpi label="Marge brute" value={margin === null ? "—" : fmtMad(margin)} tone={marginTone(marginPct)} testid="profitability-kpi-margin" />
        <Kpi label="Marge %" value={marginPct === null || marginPct === undefined ? "—" : `${marginPct}%`} tone={marginTone(marginPct)} icon={<TrendingUp size={11} />} testid="profitability-kpi-margin-pct" />
      </div>

      {(data?.warnings || []).length > 0 && (
        <div className="mt-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2" data-testid="profitability-warnings">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <div>
            {data.warnings.map((w, i) => (
              <div key={i}>• {w}</div>
            ))}
          </div>
        </div>
      )}

      {data?.by_assignment?.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowDetail((v) => !v)}
            data-testid="profitability-toggle-detail"
            className="text-xs text-zinc-600 hover:text-brand-navy inline-flex items-center gap-1"
          >
            {showDetail ? <EyeOff size={11} /> : <Eye size={11} />}
            {showDetail ? "Masquer" : "Afficher"} le détail par intervenant ({data.by_assignment.length})
          </button>
          {showDetail && (
            <div className="mt-2 border border-zinc-200 rounded-md overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]" data-testid="profitability-detail-table">
                <thead className="bg-zinc-50 text-zinc-500 font-mono">
                  <tr className="text-left text-[10px] uppercase tracking-[0.16em]">
                    <th className="px-3 py-2">Intervenant</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">Jours</th>
                    <th className="px-3 py-2 text-right">CJM</th>
                    <th className="px-3 py-2 text-right">TJM vente</th>
                    <th className="px-3 py-2 text-right">Coût</th>
                    <th className="px-3 py-2 text-right">CA</th>
                    <th className="px-3 py-2 text-right">Marge</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_assignment.map((r, i) => {
                    const rowKey = r.assignment_id || `${r.user_id}-${i}`;
                    const hasBreakdown = Array.isArray(r.monthly_breakdown) && r.monthly_breakdown.length > 0;
                    const isOpen = expanded.has(rowKey);
                    return (
                    <React.Fragment key={rowKey}>
                    <tr className="border-t border-zinc-100" data-testid={`profitability-row-${rowKey}`}>
                      <td className="px-3 py-2 text-zinc-800">
                        <div className="flex items-center gap-1.5">
                          {hasBreakdown ? (
                            <button
                              onClick={() => toggleExpand(rowKey)}
                              data-testid={`profitability-row-toggle-${rowKey}`}
                              className="p-0.5 hover:bg-zinc-100 rounded text-zinc-500"
                              title={isOpen ? "Hide monthly breakdown" : "Show monthly breakdown"}
                            >
                              {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          ) : (
                            <span className="w-[18px] inline-block" />
                          )}
                          {r.user_name || "—"}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                          {r.employment_type === "SALARIE_MA" ? "Salarié" : r.employment_type === "FREELANCE" ? "Freelance" : "Autre"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.days_count}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.cjm === null ? "—" : fmtMad(r.cjm)}</td>
                      <td className="px-3 py-2 text-right font-mono" title={data?.currency && data.currency !== "MAD" ? `${r.tjm_sell} ${data.currency}` : undefined}>
                        {fmtMad(r.tjm_sell_mad !== undefined ? r.tjm_sell_mad : r.tjm_sell)}
                        {data?.currency && data.currency !== "MAD" && r.tjm_sell > 0 && (
                          <div className="text-[9px] text-zinc-400">({r.tjm_sell} {data.currency})</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{r.cost === null ? "—" : fmtMad(r.cost)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMad(r.revenue)}</td>
                      <td className={`px-3 py-2 text-right font-mono ${r.margin === null ? "" : r.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{r.margin === null ? "—" : fmtMad(r.margin)}</td>
                    </tr>
                    {isOpen && hasBreakdown && (
                      <tr className="bg-zinc-50/60" data-testid={`profitability-breakdown-${rowKey}`}>
                        <td colSpan={8} className="px-3 py-2">
                          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-2 flex items-center gap-1.5">
                            <Calculator size={10} /> Coûts mensuels
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500">
                                <th className="px-2 py-1">Mois</th>
                                <th className="px-2 py-1 text-right">Jours</th>
                                <th className="px-2 py-1 text-right">CJM (mois)</th>
                                <th className="px-2 py-1 text-right">Coût</th>
                                <th className="px-2 py-1 text-right">CA</th>
                                <th className="px-2 py-1 text-right">Marge</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.monthly_breakdown.map((m) => (
                                <tr key={m.ym} className="border-t border-zinc-200" data-testid={`profitability-breakdown-row-${rowKey}-${m.ym}`}>
                                  <td className="px-2 py-1 font-mono text-zinc-700">{m.ym}</td>
                                  <td className="px-2 py-1 text-right font-mono">{m.days_count}</td>
                                  <td className="px-2 py-1 text-right font-mono">{fmtMad(m.cjm)}</td>
                                  <td className="px-2 py-1 text-right font-mono">{fmtMad(m.cost)}</td>
                                  <td className="px-2 py-1 text-right font-mono">{fmtMad(m.revenue)}</td>
                                  <td className={`px-2 py-1 text-right font-mono ${m.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtMad(m.margin)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, tone, icon, testid }) {
  return (
    <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-lg font-bold font-mono mt-1 ${tone || "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{hint}</div>}
    </div>
  );
}
